import { useEffect, useMemo, useState, useCallback } from "react";
import type { WidgetConfig } from "../shared/types";
import { MESSAGE_TYPES } from "../shared/messaging";
import { useWidgetConfig } from "./hooks/useWidgetConfig";
import { useConversation } from "./hooks/useConversation";
import { getParentOrigin } from "./utils/getParentOrigin";
import { WidgetDisplay } from "./components/WidgetDisplay";

function App() {
  const initialConfig = useMemo<WidgetConfig>(() => {
    const params = new URLSearchParams(window.location.search);
    const agentId = params.get("agentId");
    const apiUrl = params.get("apiUrl");
    const sourceUrl = params.get("sourceUrl") || undefined;

    if (!agentId || !apiUrl) {
      throw new Error("Missing required configuration parameters");
    }

    return { agentId, apiUrl, sourceUrl };
  }, []);

  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>(initialConfig);

  const {
    config: agentConfig,
    isLoading: isConfigLoading,
    error: configError,
  } = useWidgetConfig(widgetConfig);

  useEffect(() => {
    if (agentConfig?.organizationId && !widgetConfig.organizationId) {
      setWidgetConfig((prev) => ({
        ...prev,
        organizationId: agentConfig.organizationId,
      }));
    }
  }, [agentConfig, widgetConfig.organizationId]);

  const {
    messages,
    isLoading: isSending,
    statusMessage,
    conversationId,
    sendMessage,
    retryMessage,
  } = useConversation(widgetConfig, agentConfig?.welcomeMessage);

  const handleClose = useCallback(() => {
    const parentOrigin = getParentOrigin();
    if (!parentOrigin) return;

    window.parent.postMessage(
      {
        type: MESSAGE_TYPES.CLOSE_WIDGET,
      },
      parentOrigin,
    );
  }, []);

  useEffect(() => {
    const parentOrigin = getParentOrigin();
    if (!parentOrigin) return;

    window.parent.postMessage(
      {
        type: MESSAGE_TYPES.WIDGET_READY,
      },
      parentOrigin,
    );
  }, []);

  if (isConfigLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#CC0020] border-r-transparent"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (configError || !agentConfig) {
    return (
      <div className="flex h-full items-center justify-center bg-[#FAFAFA] p-6">
        <div className="max-w-sm text-center" role="alert">
          <svg
            className="mx-auto mb-3 h-12 w-12 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Unable to Load Chat
          </h2>
          <p className="text-sm text-gray-600">
            {configError ??
              "Failed to load chat configuration. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <WidgetDisplay
      widgetConfig={widgetConfig}
      agentConfig={agentConfig}
      messages={messages}
      isLoading={isSending}
      statusMessage={statusMessage}
      conversationId={conversationId}
      onSendMessage={sendMessage}
      onRetryMessage={retryMessage}
      onClose={handleClose}
    />
  );
}

export default App;
